import { Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@r/components/ui/button.tsx';
import { MAX_WIDGET_SLIDES } from '@r/helpers/widget-workspace-slides.ts';
import { cn } from '@r/utils/utils.ts';

interface ContentBlockSlideSwitchProps {
  activeSlide: number;
  slideCount: number;
  onSlideChange: (slideIndex: number) => void;
  onAddSlide: () => void;
  onRemoveSlide: (slideIndex: number) => void;
}

/** Barre d’onglets slides + capacité carrousel (maquette éditeur). */
export const ContentBlockSlideSwitch = ({
  activeSlide,
  slideCount,
  onSlideChange,
  onAddSlide,
  onRemoveSlide
}: ContentBlockSlideSwitchProps) => {
  const { t } = useTranslation();
  const canRemoveSlide = slideCount > 1;

  return (
    <div className="flex flex-col gap-4 pb-6 mb-8 border-b border-border/60 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap gap-2 items-center">
        {Array.from({ length: slideCount }, (_, index) => index + 1).map((slideIndex) => {
          const isActive = activeSlide === slideIndex;

          return (
            <div key={slideIndex} className="inline-flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => onSlideChange(slideIndex)}
                className={cn(
                  'inline-flex gap-2 items-center px-4 py-2 text-sm font-medium rounded-full transition-colors',
                  isActive
                    ? 'ring-1 ring-inset shadow-sm bg-muted text-foreground ring-border/60'
                    : 'bg-muted/50 text-foreground hover:bg-muted'
                )}
              >
                {t('admin.contentblock.slide.label')} {slideIndex}
                {isActive && (
                  <span className="inline-flex items-center rounded-full border-0 bg-primary px-2 py-0 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
                    {t('admin.contentblock.workspace.active')}
                  </span>
                )}
              </button>
              {canRemoveSlide && !isActive && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  rounded="full"
                  className="w-8 h-8 text-muted-foreground"
                  aria-label={`${t('admin.contentblock.slide.remove')} ${slideIndex}`}
                  onClick={() => onRemoveSlide(slideIndex)}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </Button>
              )}
            </div>
          );
        })}

        {slideCount < MAX_WIDGET_SLIDES && (
          <Button
            type="button"
            variant="outline"
            rounded="full"
            className="gap-2 bg-transparent border-2 border-dashed shadow-none border-border/80 hover:bg-muted/40"
            onClick={onAddSlide}
          >
            <Plus className="w-4 h-4" />
            {t('admin.contentblock.editor.add.slide')}
          </Button>
        )}
      </div>

      <p className="text-sm font-medium text-muted-foreground">
        {t('admin.contentblock.editor.carousel.capacity', {
          count: slideCount,
          max: MAX_WIDGET_SLIDES
        })}
      </p>
    </div>
  );
};
